import { createFileRoute, Link } from "@tanstack/react-router"
import { IconPlusFill18 } from "nucleo-ui-fill-18"
import { z } from "zod"

import type { SortOrder } from "@/lib/api/types"
import type { FirmSort } from "@/types/firm"
import { CsvExportButton } from "@/components/common/csv-export-button"
import { FirmsDirectoryToolbar } from "@/features/firms/components/firms-directory-toolbar"
import { PageHeader } from "@/components/common/page-header"
import { Button } from "@/components/ui/button"
import { FirmsTable } from "@/features/firms/components/firms-table"
import {
  defaultFirmColumns,
  firmColumnOptions,
} from "@/features/firms/firm-columns"
import { IndexColumns } from "@/components/common/index-columns"
import { useStoredColumns } from "@/hooks/use-stored-columns"
import type { CsvColumn } from "@/lib/csv"
import { firmQueries, loadAllFirms } from "@/queries/firms"
import type { FirmListItem } from "@/types/firm"

const searchSchema = z.object({
  q: z.string().optional(),
  groupBy: z.enum(["status", "kind", "risk"]).optional(),
  status: z
    .enum([
      "draft",
      "onboarding",
      "active",
      "suspended",
      "terminating",
      "terminated",
    ])
    .optional(),
  kind: z.enum(["full_ar", "iar"]).optional(),
  risk_reason: z.string().optional(),
  risk_tier: z.enum(["low", "raised", "high"]).optional(),
  gap: z.string().optional(),
  sort: z
    .enum(["legalName", "risk", "complianceScore", "appointedOn", "createdAt"])
    .optional(),
  order: z.enum(["asc", "desc"]).optional(),
})

const exportColumns: CsvColumn<FirmListItem>[] = [
  { header: "FRN", value: (firm) => firm.frn },
  { header: "Legal name", value: (firm) => firm.legalName },
  { header: "Status", value: (firm) => firm.statusLabel },
  { header: "Company number", value: (firm) => firm.companyNumber },
  {
    header: "Score",
    value: (firm) =>
      firm.complianceScore === null ? null : `${firm.complianceScore}%`,
  },
]

export const Route = createFileRoute(
  "/$organisation/_authenticated/_organisation/_main/firms/"
)({
  validateSearch: searchSchema,
  loaderDeps: ({
    search: {
      q,
      status,
      kind,
      risk_reason,
      risk_tier,
      gap,
      sort,
      order,
      groupBy,
    },
  }) => ({
    q,
    status,
    kind,
    risk_reason,
    risk_tier,
    gap,
    sort,
    order,
    group: groupBy,
  }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureInfiniteQueryData(firmQueries.infiniteList(deps)),
  component: FirmsPage,
})

function FirmsPage() {
  const { groupBy, ...filters } = Route.useSearch()
  const [visible, setVisible] = useStoredColumns(
    "firms",
    firmColumnOptions.map((option) => option.value),
    defaultFirmColumns
  )
  const navigate = Route.useNavigate()
  const sortBy = (sort: FirmSort, order: SortOrder) => {
    void navigate({
      replace: true,
      search: (current) => ({ ...current, sort, order }),
    })
  }

  return (
    <>
      <PageHeader
        title="Firms"
        actions={
          <>
            <CsvExportButton
              columns={exportColumns}
              filename="firms.csv"
              loadRows={(signal) => loadAllFirms(filters, signal)}
            />
            <Button
              nativeButton={false}
              render={
                <Link from="/$organisation" to="/$organisation/firms/create">
                  <IconPlusFill18 data-icon="inline-start" />
                  Create firm
                </Link>
              }
            />
          </>
        }
      />
      <section className="flex flex-col gap-4">
        <FirmsDirectoryToolbar
          filters={filters}
          groupBy={groupBy}
          columns={
            <IndexColumns
              options={firmColumnOptions}
              visible={visible}
              onVisibleChange={setVisible}
            />
          }
          onChange={(changes) => {
            void navigate({
              replace: true,
              search: (current) => ({ ...current, ...changes }),
            })
          }}
        />
        <FirmsTable
          filters={{ ...filters, group: groupBy }}
          visible={visible}
          onSortChange={sortBy}
        />
      </section>
    </>
  )
}
